import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, User, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/therapy", label: "Therapy" },
  { path: "/senior-living", label: "Senior Living" },
  { path: "/doctors", label: "Doctors" },
  { path: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, isAdmin, user } = useAuth();

  // Hide navbar on admin and portal pages for cleaner UX
  const isPortalPage = location.pathname === '/portal' || location.pathname === '/admin';

  return (
    <>
      <nav 
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0f0518]/80 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              data-testid="nav-logo"
              className="flex items-center gap-2"
            >
              <span className="text-xl font-bold text-[#00f0ff]">healthcentrixllc.online</span>
            </Link>

            {/* Desktop Navigation */}
            {!isPortalPage && (
              <div className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className={`nav-link text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-[#00f0ff]"
                        : "text-gray-300 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:8449436175" 
                data-testid="nav-phone"
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#00f0ff] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>844-943-6175</span>
              </a>
              
              {isAuthenticated ? (
                <Link to={isAdmin ? "/admin" : "/portal"}>
                  <Button 
                    data-testid="nav-portal"
                    className="bg-[#1a0b2e] border border-[#4b0082] text-white font-medium rounded-full px-6 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {isAdmin ? "Dashboard" : user?.first_name || "Portal"}
                  </Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button 
                    data-testid="nav-login"
                    className="bg-[#1a0b2e] border border-[#4b0082] text-white font-medium rounded-full px-6 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                </Link>
              )}
              
              <Link to="/appointment">
                <Button 
                  data-testid="nav-book-appointment"
                  className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all"
                >
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-white"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`mobile-menu ${isOpen ? "open" : ""}`}
      >
        <button
          data-testid="mobile-menu-close"
          className="absolute top-6 right-6 p-2 text-white"
          onClick={() => setIsOpen(false)}
        >
          <X className="w-8 h-8" />
        </button>
        
        <div className="flex flex-col items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              className={`text-2xl font-semibold transition-colors ${
                location.pathname === link.path
                  ? "text-[#00f0ff]"
                  : "text-white hover:text-[#00f0ff]"
              }`}
              onClick={() => setIsOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          
          {isAuthenticated ? (
            <Link to={isAdmin ? "/admin" : "/portal"} onClick={() => setIsOpen(false)}>
              <Button 
                data-testid="mobile-portal"
                className="mt-4 bg-[#1a0b2e] border border-[#4b0082] text-white font-medium rounded-full px-8 py-6 text-lg"
              >
                <User className="w-5 h-5 mr-2" />
                {isAdmin ? "Dashboard" : "My Portal"}
              </Button>
            </Link>
          ) : (
            <Link to="/login" onClick={() => setIsOpen(false)}>
              <Button 
                data-testid="mobile-login"
                className="mt-4 bg-[#1a0b2e] border border-[#4b0082] text-white font-medium rounded-full px-8 py-6 text-lg"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Login
              </Button>
            </Link>
          )}
          
          <Link to="/appointment" onClick={() => setIsOpen(false)}>
            <Button 
              data-testid="mobile-book-appointment"
              className="mt-2 bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 text-lg hover:bg-[#00c0cc] transition-all"
            >
              Book Appointment
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
}
